import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Modal1 from "../DIsplay/Modal/Modal1";
import CommonFreeTextInput from "../common/inputs/CommonFreeTextInput";
import editTicket from "../../controllers/booking/Ticket/editTicket";
import { formatMoney } from "../../features/utils/formatMoney";

function toNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) ? num : 0;
}

function sanitizeAmount(val) {
  return String(val || "")
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*)\./g, "$1");
}

function formatAmountInput(value) {
  if (!value && value !== 0) return "";
  const n = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return "";
  return n.toLocaleString();
}

function AmountInput({ label, placeholder, value, onChange }) {
  const [focused, setFocused] = useState(false);

  return (
    <CommonFreeTextInput
      label={label}
      placeholder={placeholder}
      value={focused ? value || "" : formatAmountInput(value)}
      onChange={(val) => onChange(sanitizeAmount(val))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function EditPayableForFlightModal({ open, setOpen }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [payableInput, setPayableInput] = useState("");
  const [customerPayableInput, setCustomerPayableInput] = useState("");
  const [markup, setMarkup] = useState(0);
  const [forceEditCustomerPayable, setForceEditCustomerPayable] =
    useState(false);

  const isOpen = !!open;
  const flight = open?.flightObj;

  const payable = toNumber(payableInput);
  const computedCustomerPayable = payable + markup;
  const customerPayable = forceEditCustomerPayable
    ? toNumber(customerPayableInput)
    : computedCustomerPayable;

  useEffect(() => {
    if (!isOpen) return;
    const originalPayable = toNumber(flight?.payable);
    const originalCustomerPayable = toNumber(flight?.customerPayable);
    const nextMarkup = originalCustomerPayable - originalPayable;
    setPayableInput(
      flight?.payable != null && flight?.payable !== ""
        ? String(flight.payable)
        : "",
    );
    setMarkup(nextMarkup);
    setCustomerPayableInput(String(originalCustomerPayable));
    setForceEditCustomerPayable(false);
    setLoading(false);
  }, [isOpen, flight?.payable, flight?.customerPayable]);

  const close = () => {
    if (loading) return;
    setOpen(false);
  };

  const toggleForceEdit = () => {
    setForceEditCustomerPayable((prev) => {
      const next = !prev;
      if (next) {
        setCustomerPayableInput(String(computedCustomerPayable));
      }
      return next;
    });
  };

  const handleEdit = async () => {
    const raw = String(payableInput || "").trim();

    if (!raw) {
      enqueueSnackbar("Please enter a payable amount.", { variant: "warning" });
      return;
    }

    if (Number.isNaN(payable) || !Number.isFinite(payable)) {
      enqueueSnackbar("Please enter a valid number.", { variant: "error" });
      return;
    }

    if (payable < 0) {
      enqueueSnackbar("Payable amount cannot be negative.", {
        variant: "error",
      });
      return;
    }

    if (forceEditCustomerPayable) {
      const customerRaw = String(customerPayableInput || "").trim();
      if (!customerRaw) {
        enqueueSnackbar("Please enter a customer payable amount.", {
          variant: "warning",
        });
        return;
      }
      if (Number.isNaN(customerPayable) || !Number.isFinite(customerPayable)) {
        enqueueSnackbar("Please enter a valid customer payable.", {
          variant: "error",
        });
        return;
      }
      if (customerPayable < 0) {
        enqueueSnackbar("Customer payable cannot be negative.", {
          variant: "error",
        });
        return;
      }
    }

    setLoading(true);
    const res = await editTicket({
      data: {
        payable,
        paymentAmount: payable,
        pricedAmountNgn: payable,
        customerPayable,
      },
      id: flight?._id,
    }).finally(() => setLoading(false));

    if (res?.return) {
      enqueueSnackbar("Payable updated successfully.", { variant: "success" });
      setTimeout(() => window.location.reload(), 300);
    } else {
      enqueueSnackbar("Failed to update payable. Please try again.", {
        variant: "error",
      });
    }
  };

  return (
    <Modal1 open={isOpen} setOpen={setOpen}>
      <div className="p-6 flex flex-col text-left min-w-[280px] sm:min-w-[420px]">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Edit flight payable
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Update the payable price. Customer payable keeps the original markup
          unless you force-edit it.
        </p>

        {!forceEditCustomerPayable ? (
          <div className="w-full mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            Markup kept from original price:{" "}
            <span className="font-semibold text-gray-900">
              {formatMoney(markup)}
            </span>
          </div>
        ) : null}

        <div className="w-full mb-4">
          <AmountInput
            label="Payable"
            placeholder="Enter payable amount"
            value={payableInput}
            onChange={setPayableInput}
          />
        </div>

        <div className="w-full mb-2 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-600">Customer payable</span>
          <button
            type="button"
            onClick={toggleForceEdit}
            className={`text-xs font-semibold px-2.5 py-1 rounded-md border transition ${
              forceEditCustomerPayable
                ? "bg-primary1 text-white border-primary1"
                : "bg-white text-primary1 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {forceEditCustomerPayable ? "Using force edit" : "Force edit"}
          </button>
        </div>

        {forceEditCustomerPayable ? (
          <div className="w-full mb-5">
            <AmountInput
              label=""
              placeholder="Enter customer payable"
              value={customerPayableInput}
              onChange={setCustomerPayableInput}
            />
          </div>
        ) : (
          <div className="w-full mb-5 h-12 px-4 rounded-lg border border-[#DBDBDB] bg-[#EEF2F7] text-sm text-[#1A1A1A] flex items-center justify-between">
            <span>{formatMoney(customerPayable)}</span>
            <span className="text-xs text-[#6D7C94]">payable + markup</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <button
            type="button"
            onClick={close}
            className="w-full sm:w-1/2 border border-gray-300 text-gray-700 rounded-md py-2.5 text-sm font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleEdit}
            disabled={loading}
            className="w-full sm:w-1/2 bg-primary1 text-white flex items-center justify-center rounded-md py-2.5 text-sm font-medium hover:opacity-95 transition disabled:opacity-60"
          >
            {loading ? <div className="buttonLoader" /> : <>Save</>}
          </button>
        </div>
      </div>
    </Modal1>
  );
}

export default EditPayableForFlightModal;
